/**
 * The states every screen in flows.json has to implement.
 *
 * `loading` is skeletons, never a spinner over an empty page — that rule comes
 * straight from flows.json stateRules, so it is enforced here rather than left
 * to each screen.
 */
import type { ReactNode } from 'react'
import { t, tServer, type CopyKey } from '@/lib/copy'
import { ApiFailure } from '@/lib/api'
import { isEdgeFailure } from '@/lib/edge'
import { Button } from './primitives'

export function Skeleton({ width = '100%', height = 'var(--space-md)' }: { width?: string; height?: string }) {
  return <span className="skeleton" style={{ width, height, display: 'block' }} aria-hidden="true" />
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <div className="stack-sm" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '60%' : '100%'} />
      ))}
    </div>
  )
}

export function SkeletonCard({ height = 'var(--space-xxl)' }: { height?: string }) {
  return (
    <div className="card" aria-hidden="true">
      <Skeleton height={height} />
    </div>
  )
}

export function SkeletonMetrics({ count = 4 }: { count?: number }) {
  return (
    <div className="metric-row" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div className="metric" key={i}>
          <Skeleton width="50%" height="var(--space-sm)" />
          <Skeleton width="80%" height="var(--space-lg)" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonList({ rows = 6 }: { rows?: number }) {
  return (
    <ul className="list" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i}>
          <div className="list-row list-row-static">
            <span className="avatar skeleton" />
            <div className="list-row-main">
              <Skeleton width="45%" />
              <Skeleton width="25%" height="var(--space-sm)" />
            </div>
            <Skeleton width="var(--space-xxl)" />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Screen-level loading announcement paired with whatever skeleton fits. */
export function LoadingRegion({ children }: { children: ReactNode }) {
  return (
    <div className="stack" role="status" aria-busy="true" aria-label={t('state.loading')}>
      {children}
    </div>
  )
}

export function EmptyState({
  titleKey,
  bodyKey,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
}: {
  titleKey: CopyKey
  bodyKey?: CopyKey
  actionLabel?: string
  onAction?: () => void
  /** The alternative route into the same pipeline, where one exists — an
   *  empty screen that offers upload should also offer bank linking. */
  secondaryLabel?: string
  onSecondary?: () => void
}) {
  return (
    <div className="state-block">
      <h2 className="state-title">{t(titleKey)}</h2>
      {bodyKey ? <p className="state-body">{t(bodyKey)}</p> : null}
      {actionLabel && onAction ? (
        <div className="row">
          <Button variant="primary" onClick={onAction}>
            {actionLabel}
          </Button>
          {secondaryLabel && onSecondary ? (
            <Button variant="secondary" onClick={onSecondary}>
              {secondaryLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The error state. States it plainly and offers Retry — never a status code or
 * a stack, and never `message` from error.schema.json.
 *
 * One failure never renders here: the four edge codes. flows.json
 * `globalRules.originUnreachable` puts them on the serviceUnavailable screen
 * instead, because this component's copy blames the screen ("Couldn't load
 * transactions") when the truth is that nothing at all is reachable — and a
 * user who hits four of those in a row reads it as four separate bugs. The rule
 * lives here rather than at every call site so a new screen inherits it.
 */
export function ErrorState({
  titleKey,
  error,
  onRetry,
}: {
  titleKey: CopyKey
  error?: ApiFailure
  onRetry?: () => void
}) {
  if (isEdgeFailure(error)) return null
  const detail = error ? tServer(error.copyKey) : t('error.generic')
  const retryable = error ? error.retryable : true
  return (
    <div className="state-block" role="alert">
      <h2 className="state-title">{t(titleKey)}</h2>
      <p className="state-body">{detail}</p>
      {onRetry && retryable ? (
        <Button variant="secondary" onClick={onRetry}>
          {t('cta.retry')}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * insufficientData is not an error: under two full calendar months, show what
 * is needed instead of a misleading number.
 */
export function InsufficientData({
  titleKey,
  bodyKey,
  actionLabel,
  onAction,
}: {
  titleKey: CopyKey
  bodyKey?: CopyKey
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div className="state-block">
      <h2 className="state-title">{t(titleKey)}</h2>
      {bodyKey ? <p className="state-body">{t(bodyKey)}</p> : null}
      {actionLabel && onAction ? (
        <Button variant="secondary" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  )
}

/** A search or filter matched nothing — distinct from empty, per flows.json. */
export function NoResults({
  titleKey,
  actionLabel,
  onAction,
}: {
  titleKey: CopyKey
  actionLabel: string
  onAction: () => void
}) {
  return (
    <div className="state-block">
      <p className="state-body">{t(titleKey)}</p>
      <Button variant="secondary" onClick={onAction}>
        {actionLabel}
      </Button>
    </div>
  )
}
